import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatGBP, formatUKDate } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, Lock, Sparkles } from "lucide-react";

export default function PaymentPage() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/invoice/${token}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invoice not found");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const pollStatus = useCallback(async (sid, attempts = 0) => {
    const MAX = 8;
    if (attempts >= MAX) {
      setPolling(false);
      toast.info("Still processing. Please refresh shortly.");
      return;
    }
    try {
      const { data: s } = await api.get(`/public/payments/status/${sid}`);
      if (s.payment_status === "paid") {
        setPaymentStatus("paid");
        setPolling(false);
        await load();
        toast.success("Payment received. Thank you!");
        return;
      }
      if (s.status === "expired") {
        setPaymentStatus("expired");
        setPolling(false);
        return;
      }
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sid, attempts + 1), 2500);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (sessionId) {
      setPolling(true);
      pollStatus(sessionId);
    }
  }, [sessionId, pollStatus]);

  const pay = async () => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(`/public/invoice/${token}/checkout`, { invoice_token: token, origin_url: origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Invoice not found.</div>;

  const inv = data.invoice;
  const branding = data.branding || {};
  const brandPrimary = branding.primary_color || "#1E90E6";
  const brandSecondary = branding.secondary_color || "#F2A13A";
  const bname = branding.business_name || data.business_name || "Your Cleaner";
  const paid = inv.status === "paid" || paymentStatus === "paid";

  return (
    <div className="min-h-screen bg-background flex flex-col" style={{ background: `linear-gradient(180deg, ${brandPrimary}10 0%, transparent 40%)` }}>
      <div className="max-w-xl mx-auto w-full px-6 py-12 flex-1">
        <div className="flex items-center gap-3 mb-8">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={bname} className="h-10 w-10 rounded-lg object-cover border border-border" />
          ) : (
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: brandPrimary }}>
              <Sparkles className="h-5 w-5 text-white" />
            </div>
          )}
          <div>
            <div className="font-display font-bold text-xl tracking-tight" data-testid="payment-business-name">{bname}</div>
            <div className="text-xs text-muted-foreground">Secure payment</div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="payment-card">
          {paid ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
              <h1 className="mt-4 font-display text-2xl font-bold" data-testid="payment-success">Payment received</h1>
              <p className="text-muted-foreground mt-2 text-sm">Thanks for your prompt payment. A receipt has been sent to your email.</p>
              <div className="mt-6 inline-flex items-center rounded-md px-4 py-2 font-semibold" style={{ background: brandPrimary + "15", color: brandPrimary }}>
                {formatGBP(inv.amount)}
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Amount due</div>
              <div className="mt-2 font-display text-5xl font-bold tracking-tighter" data-testid="payment-amount">{formatGBP(inv.amount)}</div>
              <div className="mt-6 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-medium">#{inv.id.slice(0, 8).toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Issued</span><span className="font-medium">{formatUKDate(inv.issued_date)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{inv.customer_name}</span></div>
              </div>
              {branding.payment_message && (
                <div className="mt-6 rounded-md p-4 text-sm" style={{ background: brandSecondary + "15", borderLeft: `3px solid ${brandSecondary}` }} data-testid="payment-message">
                  {branding.payment_message}
                </div>
              )}
              <button
                onClick={pay}
                disabled={paying || polling}
                className="mt-6 w-full rounded-md px-4 py-3 font-semibold text-white disabled:opacity-50 transition active:scale-[0.98]"
                style={{ background: brandPrimary }}
                data-testid="pay-now-button"
              >
                {paying ? "Redirecting…" : polling ? "Checking status…" : `Pay ${formatGBP(inv.amount)} securely`}
              </button>
              <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" /> Card, Apple Pay & Google Pay · Secured by Stripe
              </div>
            </>
          )}
        </div>
      </div>
      <footer className="max-w-xl mx-auto w-full px-6 py-6 text-xs text-muted-foreground text-center">
        Powered by WindowIQX · {bname} handles your cleaning
      </footer>
    </div>
  );
}
